import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, forkJoin, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ApPersistenceService } from '@ap/persistence';

import { WebcomponentEvent } from './webcomponent.model';
import { ApWebcomponentService } from './webcomponent.service';

@Component({
    template: '',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ApWebcomponentComponent implements OnInit, OnDestroy {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly elementRef = inject(ElementRef);
    private readonly persistenceService = inject(ApPersistenceService);
    private readonly renderer = inject(Renderer2);
    private readonly router = inject(Router);
    private readonly webcomponentService = inject(ApWebcomponentService);

    private subscription: Subscription;
    private identifier: string;
    private webcomponentEvents: WebcomponentEvent[];

    ngOnInit(): void {
        this.subscription = combineLatest([this.activatedRoute.data, this.activatedRoute.params, this.activatedRoute.queryParams])
            .pipe(
                switchMap(([data, params, queryParams]) => {
                    this.identifier = data.identifier;
                    this.webcomponentEvents = data.events || [];
                    this.persistenceService.setSession(this.identifier, 'path', data.path);
                    this.persistenceService.setSession(this.identifier, 'params', params);
                    this.persistenceService.setSession(this.identifier, 'queryParams', queryParams);

                    return this.webcomponentService.getConfiguration(data.deployUrl).pipe(
                        switchMap((config) => {
                            config.deployUrl = data.deployUrl;
                            return of(config);
                        })
                    );
                }),
                switchMap((config) => {
                    const scripts = config.scripts.map((script) =>
                        this.webcomponentService.addScriptTag(script, config.deployUrl, this.elementRef, this.renderer)
                    );
                    const hasStyle = !!config.style;
                    const style =
                        hasStyle &&
                        this.webcomponentService.addStyleSheet(`${config.deployUrl}/${config.style}`, this.elementRef, this.renderer);
                    return forkJoin({
                        scripts: forkJoin(scripts),
                        ...(hasStyle ? { style } : {}),
                        config: of(config)
                    });
                })
            )
            .subscribe(({ config }) => {
                this.webcomponentService.addWebcomponentTag(config.tagName, this.elementRef, this.renderer);
                this.webcomponentEvents.forEach((customEvent: WebcomponentEvent) => {
                    this.webcomponentService.addWebcomponentEventListener(config.tagName, customEvent);
                });
                this.webcomponentService.listenToWebcomponentRouteChangeEvent(config.tagName, this.router.config);
            });
    }

    ngOnDestroy(): void {
        this.webcomponentService.clear();
        this.subscription.unsubscribe();
        this.persistenceService.clearSession(this.identifier);
    }
}
